import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, CheckCircle2, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = 'https://yourpersonalshopper-storage.sfo3.digitaloceanspaces.com/omniread/omniread_logo.webp';

export default function ResetPassword() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!token) {
            toast.error('Invalid or missing reset token');
            return;
        }

        if (!password || !confirmPassword) {
            toast.error('Please fill in all fields');
            return;
        }

        if (password !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        if (password.length < 8) {
            toast.error('Password must be at least 8 characters');
            return;
        }

        setLoading(true);
        try {
            const resp = await axios.post(`${API}/auth/reset-password`, {
                token,
                password
            });

            if (resp.data.success) {
                setSubmitted(true);
                toast.success('Password reset successfully!');
            } else {
                toast.error(resp.data.detail || 'Failed to reset password');
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to reset password. The link may have expired.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    if (submitted) {
        return (
            <div className="page-container flex flex-col items-center justify-center min-h-screen p-4 md:p-12 overflow-x-hidden">
                <div className="fade-in max-w-[450px] w-full text-center">
                    <div className="mystical-card py-12 px-8">
                        <CheckCircle2 size={64} className="text-[#d4af37] mx-auto mb-6" />
                        <h2 className="gold-text text-3xl mb-4">
                            Password Reset
                        </h2>
                        <p className="text-gray-400 mb-8 text-base leading-relaxed">
                            Your password has been successfully updated. You can now login with your new credentials.
                        </p>
                        <Button
                            onClick={() => navigate('/login')}
                            className="btn-gold w-full"
                        >
                            Go to Login
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container flex flex-col items-center justify-center min-h-screen p-4 md:p-12 overflow-x-hidden">
            <div className="fade-in max-w-[450px] w-full">
                {/* Logo */}
                <div className="mb-8 cursor-pointer text-center" onClick={() => navigate('/')}>
                    <img
                        src={LOGO_URL}
                        alt="OmniRead.AI"
                        className="w-24 h-24 md:w-32 md:h-32 mx-auto block drop-shadow-xl"
                    />
                </div>

                <h1 className="gold-text mb-2 text-3xl md:text-5xl font-bold tracking-tighter text-center uppercase">
                    Reset Password
                </h1>

                <p className="text-sm md:text-base text-gray-500 mb-8 text-center lowercase tracking-wide">
                    Create a new secure password for your account
                </p>

                <div className="mystical-card">
                    <form onSubmit={handleSubmit}>
                        <div className="mb-6 text-left">
                            <Label htmlFor="password" className="block mb-2">
                                New Password
                            </Label>
                            <div className="relative">
                                <Lock size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600" />
                                <Input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Enter new password"
                                    className="pl-12 pr-12 bg-black/40 border-gold/30 text-base"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-600 hover:text-[#d4af37] transition-colors"
                                >
                                    {showPassword ? (
                                        <EyeOff size={20} />
                                    ) : (
                                        <Eye size={20} />
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className="mb-8 text-left">
                            <Label htmlFor="confirmPassword" className="block mb-2">
                                Confirm New Password
                            </Label>
                            <div className="relative">
                                <Lock size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600" />
                                <Input
                                    id="confirmPassword"
                                    type={showConfirmPassword ? "text" : "password"}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Confirm new password"
                                    className="pl-12 pr-12 bg-black/40 border-gold/30 text-base"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-600 hover:text-[#d4af37] transition-colors"
                                >
                                    {showConfirmPassword ? (
                                        <EyeOff size={20} />
                                    ) : (
                                        <Eye size={20} />
                                    )}
                                </button>
                            </div>
                        </div>

                        <Button
                            type="submit"
                            className="btn-gold w-full mb-6"
                            disabled={loading}
                        >
                            {loading ? 'Updating...' : 'Reset Password'}
                        </Button>
                    </form>

                    <div className="text-center">
                        <Link
                            to="/login"
                            className="text-gold no-underline text-sm flex items-center justify-center gap-2 hover:text-[#f4d03f] transition-colors font-bold"
                        >
                            <ArrowLeft size={16} />
                            Back to Login
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
